import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./../ui/dialog";
import { Share2, Copy, Check, Loader2, Heart } from "lucide-react";
import { createSavedExperience } from "../../lib/adventureApi";
import { getUser, isTradeUser } from "../../lib/auth";

export default function SaveExperienceDialog({ tourSlug, tourName, triggerClassName, triggerLabel = "Save & share this experience" }) {
  const [open, setOpen] = useState(false);
  const [clientName, setClientName] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const partner = isTradeUser();

  const save = async () => {
    setSaving(true);
    try {
      const user = getUser();
      const payload = {
        tour_slug: tourSlug,
        owner_id: partner && user ? user.id : null,
        client_name: clientName || null,
        note: note || null,
      };
      const res = await createSavedExperience(payload);
      const url = `${window.location.origin}/experience/${res.token}`;
      setLink(url);
    } catch (_) {
      toast.error("Could not create your experience link");
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch (_) {
      const ta = document.createElement("textarea");
      ta.value = link; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: tourName, text: `Take a look at this journey: ${tourName}`, url: link }); }
      catch (_) { /* cancelled */ }
    } else { copy(); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setLink(""); setCopied(false); } }}>
      <DialogTrigger asChild>
        <button data-testid="save-experience-btn" className={triggerClassName}>
          <Heart className="w-4 h-4" /> {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-xl tracking-tight">Keep this experience</DialogTitle>
        </DialogHeader>
        {!link ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-secondary leading-relaxed">
              Save this journey as your own shareable page — come back to it anytime and send it to family and friends.
              {partner && " It'll carry your branding for your client."}
            </p>
            {partner && (
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-ink-muted">Client name (optional)</label>
                <input data-testid="experience-client-name" value={clientName} onChange={(e) => setClientName(e.target.value)}
                  placeholder="e.g. The Thompson family"
                  className="w-full mt-1 h-11 px-3 rounded-sm border border-sand-dark text-ink text-sm focus:border-moss outline-none" />
              </div>
            )}
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-ink-muted">A personal note (optional)</label>
              <textarea data-testid="experience-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Add a message to remember this by…"
                className="w-full mt-1 px-3 py-2 rounded-sm border border-sand-dark text-ink text-sm focus:border-moss outline-none resize-none" />
            </div>
            <button data-testid="experience-create" onClick={save} disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-11 rounded-sm font-semibold transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />} Create my experience link
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="experience-link-ready">
            <p className="text-sm text-ink-secondary">Your experience is saved. Share it or bookmark it — it lives on whether or not you book.</p>
            <div className="flex items-center gap-2 bg-sand rounded-sm p-2">
              <input readOnly value={link} className="flex-1 bg-transparent text-xs text-ink px-2 outline-none" data-testid="experience-link-input" />
              <button onClick={copy} data-testid="experience-copy" className="flex items-center gap-1.5 bg-ink text-white px-3 h-9 rounded-sm text-xs font-semibold">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button onClick={nativeShare} data-testid="experience-share"
              className="w-full flex items-center justify-center gap-2 border border-sand-dark hover:border-moss h-11 rounded-sm font-medium text-sm transition-colors">
              <Share2 className="w-4 h-4 text-moss" /> Share
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
